/*
 * @Author: yanghailong
 * @Date: 2023-08-23 09:06:30
 * @Last Modified by: yanghailong
 * @Last Modified time: 2025-03-27 11:31:35
 */

import { randomUUID, mixin } from './utils/index.js'
import Konva from 'konva'
import { GraphElementPort } from "./graph-element-port.js"
import { AddObjectRecord, SetPositionRecord } from './history/record.js'
import { Property } from "./element/property.js"
import { getImgSrcByName } from './utils/img-load.js'


class GraphElement extends Property {
  static create(...args) {
    return new this(...args)
  }

  static fromJSON(topology, node, complete) {
    return new Promise((resolve) => {
      let elem = this.create(topology, node.type, node.attrs.x, node.attrs.y, {
        text: node.label,
        port: true,
      })

      // set  uuid
      elem.uuid = node.uuid
      // set other attrs
      elem.deviceIpAddress = node.deviceIpAddress
      elem.deviceUUID = node.deviceUUID


      elem.onLoad(() => {
        elem.label.setAttr('text', node.label)
        // set port uuid
        elem.ports.portDown.setAttr('uuid', node.ports.portDown)
        elem.ports.portUp.setAttr('uuid', node.ports.portUp)
        elem.setPropertyVisible(node.showProperties)
        // add element
        topology.storage.addElement(elem)
        topology.history.push(new AddObjectRecord(topology, elem))

        complete && complete()
        resolve(elem)
      })

    })
  }


  /**
   * 元素构造器
   * @mixes Property
   * @param {import('./topology.js').Topology} topology
   * @param {string} name 元素名称
   * @param {number} x 初始x
   * @param {number} y 初始y
   * @param {object} attrs 其他属性
   * @param {string} attrs.text 元素中文名称
   * @param {boolean} attrs.port 是否显示端口
   * @param {boolean} attrs.useTransparentImg 是否使用透明底座
   * @param {number} attrs.width 底座宽度
   * @param {number} attrs.height 底座高度
   *
   */
  constructor(topology, name, x = 0, y = 0, attrs = {}) {
    super()
    this.__topology = topology
    this.uuid = randomUUID()
    this.onLoadCallbacks = []
    this.onRemoveCallbacks = []
    this.keyShape = null
    this.portSize = 6
    this.g = new Konva.Group({
      x: x,
      y: y,
      draggable: true,
      name: name,
    })
    this.g.__ge = this
    this.deviceUUID = null
    this.deviceIpAddress = null


    this.setup(name, attrs)
  }
 
  setup(name, attrs) {

    this.addKeyShape(name, attrs)

    // keyShape加载完成后，添加其他组件
    this.onLoad(() => {
      this.addPort(attrs.port)
      this.addLabel(attrs.text)
      this.addTransformer()
      // this.bindDragEvent()
    })
  }


  /**
   * 添加底座
   * @param {string} name 图形名称 如 'breaker' 'bus' 等
   * @param {object} attrs 属性
   */
  addKeyShape(name, attrs) {
    // 使用透明底座
    if (attrs.useTransparentImg) {
      const width = 36
      const height = 50

      this.keyShape = new Konva.Rect({
        x: 0,
        y: 0,
        width: attrs.width || width,
        height: attrs.height || height,
        // stroke: '#AFB7FD',
        dash: [5, 5],
        isKeyShape: true,
        name: `${name}-img`,
      })

      this.g.add(this.keyShape)

      setTimeout(() => {
        // 确保onLoad回调在keyShape加载完成后执行
        this.executeLoadedHook(this.keyShape)
      })

    } else {

      // let imgSrc = `/img/${name}.png`
      let imgSrc = getImgSrcByName(name)

      Konva.Image.fromURL(imgSrc, (darthNode) => {
        // log('darthNode', darthNode)
        darthNode.setAttrs({
          x: 0,
          y: 0,
          isKeyShape: true,
          name: `${name}-img`,
          ...attrs,
        })
        this.keyShape = darthNode
        this.g.add(darthNode)
        this.executeLoadedHook(darthNode)
      })
    }
  }

  /**
   * 元素端口
   * @param {*} hasPort
   */
  addPort(hasPort) {
    if (hasPort) {
      this.ports = new GraphElementPort(0, 0, this.width, this.height, this.portSize)
      this.g.add(this.ports.portUp)
      this.g.add(this.ports.portDown)

      if (this.__topology.editor) {
        this.bindPortEvent()
      }
    }
  }

  removePort(port) {
    this.ports.remove(port)
  }

  bindPortEvent() {
    this.g.on('mouseover', (ev) => {
      this.ports.show()
    })
    this.g.on('mouseout', (ev) => {
      this.ports.hide()
    })
    this.ports.portDown.on('mousedown', () => {
      log('port mousedown')
      let pos = this.stage.getPointerPosition()
      pos.x -= this.stage.x()
      pos.y -= this.stage.y()

      ///////////////////////////////////
      this.g.draggable(false)
      this.stage.fire('start-drawline', {
        port: this.ports.portDown,
        pos: pos,
        sourceGraphElement: this,
      })
    })
  }

  bindDragEvent() {
    const topology = this.__topology
    const { history, painter } = topology

    const oldPos = { x: 0, y: 0 }

    this.g.on('dragstart', (event) => {
      let pos = event.target.absolutePosition()
      oldPos.x = pos.x
      oldPos.y = pos.y
    })

    // 元素移动时 更新连线位置
    this.g.on('dragmove', (event) => {
      painter.update()
    })

    this.g.on('dragend', (event) => {
      let newPos = event.target.absolutePosition()
      console.log('newPos', newPos)
      history.push(new SetPositionRecord(topology, event.target, newPos, oldPos))
    })
  }

  /**
   * 元素名称Label
   * @param {*} text
   */
  addLabel(text) {
    // Label宽度120px
    const LabelWidth = 120
    // Label距离图形顶部的距离5px
    const LabelTop = this.height + 5

    this.label = new Konva.Text({
      x: this.width / 2 - LabelWidth / 2,
      y: LabelTop,
      width: LabelWidth,
      text: text,
      fill: 'rgba(0, 0, 0, 0.6)',
      align: 'center',
      verticalAlign: 'bottom',
      fontSize: 12,
      // visible: false,
    })
    this.g.add(this.label)
  }

  /**
   * 设置元素名称
   * @param {string} text 文本
   */
  setLabel(text) {
    this.label.text(text)
  }
  /**
   * 设置UUID
   * @param {string} uuid 
   */
  setUUID(uuid) {
    this.deviceUUID = uuid
  }
  /**
   * 设置IP地址
   * @param {string} ip 
   */
  setIp(ip) {
    this.deviceIpAddress = ip
  }

  /**
   * 添加元素加载成功的回调函数
   * @param {Function} callback
   */
  onLoad(callback) {
    this.onLoadCallbacks.push(callback)
  }

  /**
   * 添加元素移除时的回调函数
   * @param {Function} callback
   */
  onRemove(callback) {
    this.onRemoveCallbacks.push(callback)
  }

  /**
   * 执行加载完成的回调函数
   */
  executeLoadedHook(darthNode) {
    this.onLoadCallbacks.forEach(callback => {
      callback(darthNode)
    })
  }

  /**
   * 执行移除元素的回调函数
   */
  executeRemovedHook() {
    this.onRemoveCallbacks.forEach(callback => {
      callback()
    })
  }

  /**
   * 更新端点位置
   */
  updatePortPosition() {
    let x = this.g.x()
    let y = this.g.y()
    this.ports.update(x, y, this.width, this.height)
  }

  addTransformer() {
    const { storage } = this.__topology

    let tr = this.layer.findOne('#el-tr')
    if (!tr) {
      tr = new Konva.Transformer({
        rotateEnabled: false,
        resizeEnabled: false,
        padding: 5,
        id: 'el-tr'
      })
      this.layer.add(tr)
    }
    this.g.on('click', () => {
      storage.select(this)
      // 此处可以选择keyshape或者g作为移动的对象
      tr.nodes([this.g])
      this.layer.draw()
    })
    this.stage.on('click', (event) => {
      if (event.target === this.stage) {
        tr.nodes([])
        storage.deselect()
      }
    })
  }

  /**
   * 将该元素添加到图层
   * @param {import('konva').default.Layer} layer
   */
  addToLayer(layer) {
    layer.add(this.g)
  }

  toJSON() {
    return {
      type: this.g.attrs.name,
      uuid: this.uuid,
      deviceIpAddress: this.deviceIpAddress,
      deviceUUID: this.deviceUUID,
      label: this.label.attrs.text,
      attrs: this.g.attrs,
      ports: this.ports.toJSON(),
      keyShapeAttrs: this.keyShape.attrs,
      showProperties: this.prop_is_show,
    }
  }

  // 复制JSON
  copyJSON() {
    return {
      ...this.toJSON(),
      uuid: randomUUID(),
      deviceIpAddress: null,
      deviceUUID: null,
      ports: {
        portDown: randomUUID(),
        portUp: randomUUID(),
      }
    }
  }

  /**
   * 设置属性的可见性
   * @param {boolean} isVisible 
   */
  setPropertyVisible(isVisible) {
    this.prop_is_show = isVisible
    for (let propLabel of this.properties.values()) {
      propLabel.visible(isVisible)
    }

    this.layer.draw()
  }


  /**
   * 获取该元素的所有入边
   */
  getIncomingEdges() {
    return this.__topology.storage.graph.edges.filter(edge => edge.target.node === this)
  }

  /**
   * 获取该元素的所有出边
   */
  getOutgoingEdges() {
    return this.__topology.storage.graph.edges.filter(edge => edge.source.node === this)
  }

  /**
   * 获取与节点相连接的边。
   */
  getConnectedEdges() {
    return [...this.getIncomingEdges(), ...this.getOutgoingEdges()]
  }


  _format(value) {
    let v = typeof value === 'number' ? value.toFixed(2) : value
    return v
  }



  get x() {
    return this.g.x()
  }
  get y() {
    return this.g.y()
  }
  get width() {
    return this.keyShape.width()
  }
  get height() {
    return this.keyShape.height()
  }
  /**
   * 元素中心点x坐标
   */
  get cx() {
    return this.x + this.width / 2
  }
  /**
   * 元素中心点y坐标
   */
  get cy() {
    return this.y + this.height / 2
  }

  get stage() {
    return this.__topology?.storage?.stage
  }

  get layer() {
    return this.__topology?.storage?.layer
  }

  get type() {
    return this.g.attrs.name
  }
  get name() {
    if (this.label) {
      return this.label.attrs.text
    }
  }
}

// mixin(GraphElement.prototype, Property.prototype)

export { GraphElement }
